import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { useBranding } from "@/lib/branding";
import { Loader2, Mail, Lock, Sparkles, Eye, EyeOff, X, KeyRound, CheckCircle2, ArrowRight } from "lucide-react";

/**
 * iter57 — Configurable login cover image.
 * iter63 — Cover image is now sourced dynamically from Admin Settings via
 *          GET /api/public/branding, so the studio owner can swap portfolio
 *          shots without a redeploy. `?cover=<url>` query param still wins
 *          for on-the-fly previews; env var + shipped default remain as
 *          fallbacks inside `useBranding()`.
 */

export default function Login() {
  const { login } = useAuth();
  const branding = useBranding();
  const LOGIN_COVER_URL = branding.cover_image_url;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm fade-in">
          <div className="flex items-center gap-2 mb-10">
            <Sparkles className="w-6 h-6 text-rose-700" strokeWidth={1.8} />
            <span className="font-heading text-lg font-semibold tracking-tight">Kahani by Rakesh Janardan</span>
          </div>
          <h1 className="font-heading text-3xl font-semibold mb-2">Welcome back</h1>
          <p className="text-stone-500 mb-8 text-sm">Sign in to manage bookings & post-production.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  className="input !pl-10"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@studio.com"
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Password</label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs text-rose-700 hover:text-rose-900 hover:underline"
                  data-testid="login-forgot-password"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  className="input !pl-10 !pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.6} /> : <Eye className="w-4 h-4" strokeWidth={1.6} />}
                </button>
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {err}
              </div>
            )}

            <button data-testid="login-submit-button" type="submit" disabled={loading} className="btn btn-primary w-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign in
            </button>
          </form>

          {/* iter62 — CTA linking clients to the public inquiry form. Kept
              small + editorial so it never competes with the sign-in flow
              for existing staff. */}
          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Are you a client?</div>
            <a
              href="/inquiry"
              className="inline-flex items-center gap-1.5 text-sm text-rose-700 hover:text-rose-900 font-semibold"
              data-testid="login-inquiry-cta"
            >
              Not a client yet? Get a quote
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>

      <div
        className="hidden md:block flex-1 relative bg-cover bg-center"
        style={{ backgroundImage: `url('${LOGIN_COVER_URL}')` }}
        data-testid="login-cover-image"
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-rose-900/60 via-rose-800/30 to-amber-700/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.25em] mb-3 opacity-90">For modern studios</div>
          <h2 className="font-heading text-4xl leading-tight font-semibold max-w-md">Run weddings & shoots with the calm of a checklist.</h2>
          <p className="mt-3 text-white/80 text-sm max-w-md">Bookings, deliveries, payments — all in one place. Replace your spreadsheets, keep the spirit.</p>
        </div>
      </div>

      {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} />}
    </div>
  );
}

// ------------------------------------------------------------------
// Forgot-password multi-step modal.
// Step 1: user enters their email → we tell them the studio admin needs to issue a link.
// Step 2: user pastes the one-time token/link the admin shared over WhatsApp.
// Step 3: user sets a new password and it's applied.
// ------------------------------------------------------------------
function ForgotPasswordModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [tokenEmail, setTokenEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submitStep1 = (e) => {
    e.preventDefault();
    setErr("");
    setStep(2);
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const raw = extractToken(token);
      const { data } = await axios.post(`${API}/auth/verify-reset-token`, { token: raw });
      setToken(raw);
      setTokenEmail(data.email);
      setStep(3);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const submitStep3 = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (newPw !== confirmPw) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPw });
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4 fade-in" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="forgot-password-modal">
        <div className="flex items-center justify-between px-6 h-14 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
            <h2 className="font-heading font-semibold">Reset your password</h2>
          </div>
          <button className="btn-ghost p-2 rounded" onClick={onClose} aria-label="Close" data-testid="forgot-close"><X className="w-5 h-5" strokeWidth={1.6} /></button>
        </div>

        <div className="px-6 py-3 flex items-center gap-2 text-[11px] uppercase tracking-wider text-stone-500 font-semibold border-b border-stone-100">
          <StepDot n={1} label="Your email" current={step} done={step > 1} />
          <ArrowRight className="w-3 h-3 text-stone-300" />
          <StepDot n={2} label="Enter link" current={step} done={step > 2} />
          <ArrowRight className="w-3 h-3 text-stone-300" />
          <StepDot n={3} label="New password" current={step} done={done} />
        </div>

        {done ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" strokeWidth={1.6} />
            </div>
            <div className="font-heading font-semibold">Password updated</div>
            <div className="text-sm text-stone-500 mt-1">Sign in with your new password for {tokenEmail}.</div>
            <button className="btn btn-primary btn-sm mt-4" onClick={onClose} data-testid="forgot-done">Back to sign in</button>
          </div>
        ) : step === 1 ? (
          <form onSubmit={submitStep1} className="p-6 space-y-4">
            <p className="text-sm text-stone-600">
              For your security, only the studio admin can issue reset links. Enter your email so you know who to contact,
              then have the admin generate a one-time link from the Users page and share it with you (over WhatsApp).
            </p>
            <div>
              <label className="label">Email</label>
              <input required type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" autoFocus data-testid="forgot-email-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" data-testid="forgot-step1-next">I have the link →</button>
            </div>
          </form>
        ) : step === 2 ? (
          <form onSubmit={submitStep2} className="p-6 space-y-4">
            <p className="text-sm text-stone-600">
              Paste the reset link or the token the admin shared with you. The link is valid for one hour after it's created.
            </p>
            <div>
              <label className="label">Reset link or token</label>
              <input required type="text" className="input font-mono text-xs" value={token} onChange={(e) => setToken(e.target.value)} placeholder="https://…/reset-password?token=…" autoFocus data-testid="forgot-token-input" />
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="forgot-error">{err}</div>}
            <div className="flex justify-between pt-2">
              <button type="button" className="text-xs text-stone-500 hover:text-stone-800" onClick={() => setStep(1)}>← Back</button>
              <button type="submit" className="btn btn-primary" disabled={busy} data-testid="forgot-step2-next">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify link
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitStep3} className="p-6 space-y-4">
            <p className="text-sm text-stone-600">
              Setting a new password for <span className="font-semibold text-stone-800">{tokenEmail}</span>.
            </p>
            <PwField label="New password" value={newPw} setValue={setNewPw} show={showPw} setShow={setShowPw} testId="forgot-new-password" />
            <PwField label="Confirm new password" value={confirmPw} setValue={setConfirmPw} show={showPw} setShow={setShowPw} testId="forgot-confirm-password" />
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="forgot-error">{err}</div>}
            <div className="flex justify-between pt-2">
              <button type="button" className="text-xs text-stone-500 hover:text-stone-800" onClick={() => setStep(2)}>← Back</button>
              <button type="submit" className="btn btn-primary" disabled={busy} data-testid="forgot-step3-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update password
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function StepDot({ n, current, label, done }) {
  const state = done ? "done" : current === n ? "active" : "idle";
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
        state === "done" ? "bg-emerald-600 text-white"
        : state === "active" ? "bg-rose-700 text-white"
        : "bg-stone-200 text-stone-500"
      }`}>
        {state === "done" ? <CheckCircle2 className="w-3 h-3" /> : n}
      </span>
      <span className={state === "active" ? "text-rose-700" : "text-stone-500"}>{label}</span>
    </div>
  );
}

function PwField({ label, value, setValue, show, setShow, testId }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <Lock className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
        <input required type={show ? "text" : "password"} className="input !pl-10 !pr-10" value={value} onChange={(e) => setValue(e.target.value)} data-testid={testId} minLength={6} />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700" tabIndex={-1}>
          {show ? <EyeOff className="w-4 h-4" strokeWidth={1.6} /> : <Eye className="w-4 h-4" strokeWidth={1.6} />}
        </button>
      </div>
    </div>
  );
}

function extractToken(raw) {
  const s = (raw || "").trim();
  try {
    if (s.startsWith("http")) {
      const u = new URL(s);
      return u.searchParams.get("token") || s;
    }
  } catch (err) {
    // Not a valid URL — fall through and treat the whole input as the token.
    if (typeof console !== "undefined") {
      console.debug("extractToken: not a URL, using raw:", err?.message);
    }
  }
  return s;
}
